import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Tag, Dropdown, Badge, Tooltip, App, theme, Checkbox } from 'antd';
import type { TableProps } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  MoreOutlined,
  EditOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useRequest } from 'ahooks';

import { useVendorsTable, useVendorMutations, useAllCategoriesForColorMap } from '@/hooks';
import { useListPage, createCountryCounter } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import type { VendorListItem } from '@/types/api';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import CategoryTag from '@/components/common/CategoryTag';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type {
  FieldConfig,
  FilterValues,
  CascaderOption,
} from '@/components/common/GenericFilterPopover';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useVendorStore } from '@/stores';
import type { VendorFilters } from '@/stores/vendorStore';
import { categoryApi, locationApi } from '@/services/api';
import { buildCategoryColorMap, normalizeVendorCategories } from '@/utils/categoryUtils';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { HighlightText } from '@/components/common/HighlightText';

interface VendorListPageProps {
  compact?: boolean; // If true, hide header and filters
  initialCategories?: number[]; // Pre-filter by category IDs
  showCategoryColumn?: boolean; // If false, hide category column
  defaultActiveOnly?: boolean; // If true, default to showing only active vendors
}

/** Popover-local filter value shape for the vendor list */
interface VendorFilterPopoverValues extends FilterValues {
  categories: string[];
  country: string[][];
  status?: string;
}

// Parse locations string to cascader format
// "44-2-2,110-3,158" -> [["44", "44-2", "44-2-2"], ["110", "110-3"], ["158"]]
// countryId-stateId-cityId format
const parseLocationsString = (locationsStr: string): string[][] => {
  if (!locationsStr || !locationsStr.trim()) return [];

  return locationsStr.split(',').map((location) => {
    const parts = location.trim().split('-');
    if (parts.length === 1) {
      return [parts[0]];
    } else if (parts.length === 2) {
      return [parts[0], `${parts[0]}-${parts[1]}`];
    }
    return [parts[0], `${parts[0]}-${parts[1]}`, `${parts[0]}-${parts[1]}-${parts[2]}`];
  });
};

const VendorListPage = ({
  compact = false,
  initialCategories,
  showCategoryColumn = true,
  defaultActiveOnly = false,
}: VendorListPageProps = {}) => {
  const navigate = useNavigate();
  const { t, i18n } = useVendorTranslation();
  const { token } = theme.useToken();
  const { message } = App.useApp();

  // Use Zustand store for filters
  const { filters, setSearch, setCategoryIds, setCountry, setIsActive, resetFilters } =
    useVendorStore();

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  // Confirmation dialog for activate/deactivate vendor
  const toggleStatusDialog = useConfirmationDialog<VendorListItem>();

  // Set initial category filter when provided, or reset when not in compact mode
  useEffect(() => {
    if (initialCategories && initialCategories.length > 0) {
      // In compact mode (e.g., CategoryDetailPage), apply the initial filter
      setCategoryIds(initialCategories);
    } else if (!compact) {
      // In full mode (main vendor list), reset category filter to show all vendors
      setCategoryIds([]);
    }
  }, [initialCategories, compact, setCategoryIds]);

  // Set active-only filter when defaultActiveOnly is enabled (e.g., in category detail page)
  useEffect(() => {
    if (defaultActiveOnly) {
      setIsActive(true);
    }
  }, [defaultActiveOnly, setIsActive]);

  // Badge parity with the pre-S0.3 hand-rolled count:
  // - categoryIds: one per selected category (not counted in compact mode)
  // - country: one per comma-separated location
  // - isActive: counted whenever it deviates from the Active default
  // - search: never counted in the badge
  const customFilterCounters = useMemo(
    () => ({
      categoryIds: (value: unknown) => (compact ? 0 : Array.isArray(value) ? value.length : 0),
      country: createCountryCounter,
      isActive: (value: unknown) => (value === true ? 0 : 1),
    }),
    [compact]
  );

  // S0.3 list-kit: shared table wiring + filter badge count + state triad
  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    VendorFilters,
    VendorListItem
  >({
    useTableHook: useVendorsTable as (filters: VendorFilters) => TableHookResult<VendorListItem>,
    filters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  // Fetch ALL categories for color mapping (no typeIds filter to get colors for all categories)
  const { categories: allCategories } = useAllCategoriesForColorMap();

  // Build category color map for efficient lookups
  const categoryColorMap = useMemo(() => buildCategoryColorMap(allCategories), [allCategories]);

  // Active categories for the filter popover options (parity with the retired vendor FilterPopover)
  const { data: filterCategoryData } = useRequest(
    () => categoryApi.list({ isActive: 1, pageSize: -1 }),
    {
      cacheKey: '/api/categories?isActive=1&pageSize=-1',
    }
  );

  // Hierarchical locations for the filter cascader (parity with the retired vendor FilterPopover)
  const { data: locationsData, loading: isLoadingLocations } = useRequest(
    () =>
      locationApi.getAllLocations({
        lang: i18n.language as 'en' | 'zh',
        typeId: 1, // Vendor type
      }),
    {
      cacheKey: `/api/locations/all?lang=${i18n.language}&typeId=1`,
      staleTime: 30 * 60 * 1000, // 30 minutes
      refreshOnWindowFocus: false,
    }
  );

  const cascaderOptions: CascaderOption[] = useMemo(() => {
    if (!locationsData || !locationsData.data || locationsData.data.length === 0) return [];

    // Build Country → State → City hierarchy using countryId (not countryCode)
    return locationsData.data.map((country) => ({
      value: country.countryId.toString(),
      label: country.countryName,
      children: country.states.map((state) => ({
        value: `${country.countryId}-${state.stateId}`,
        label: state.stateName,
        children: state.cities.map((city) => ({
          value: `${country.countryId}-${state.stateId}-${city.cityId}`,
          label: city.cityName,
          isLeaf: true,
        })),
      })),
    }));
  }, [locationsData]);

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'categories',
        label: t('vendor.filter.category'),
        type: 'multiselect-with-tags',
        options: (filterCategoryData?.data || []).map((cat) => ({
          label: cat.category,
          value: cat.categoryId.toString(),
          color: cat.color,
        })),
        searchPlaceholder: t('vendor.filter.selectCategories'),
        noSelectionText: t('vendor.filter.noCategoriesSelected'),
        noDataText: t('vendor.filter.noCategoriesAvailable'),
        noSearchResultText: t('vendor.filter.noSearchResult'),
        resetText: t('vendor.filter.reset'),
      },
      {
        key: 'country',
        label: t('vendor.filter.location'),
        type: 'cascader',
        options: cascaderOptions,
        placeholder: t('vendor.filter.searchOrSelectLocation'),
        loading: isLoadingLocations,
        loadingText: t('vendor.filter.loadingLocations'),
        noDataText: t('vendor.filter.noLocationsAvailable'),
        noSearchResultText: t('vendor.filter.noSearchResult'),
        resetText: t('vendor.filter.reset'),
      },
      {
        key: 'status',
        label: t('vendor.filter.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'active', label: t('vendor.status.active') },
          { value: 'inactive', label: t('vendor.status.inactive') },
          { value: 'all', label: t('vendor.status.all') },
        ],
        resetText: t('vendor.filter.reset'),
      },
    ],
    [t, filterCategoryData, cascaderOptions, isLoadingLocations]
  );

  const initialPopoverFilters: VendorFilterPopoverValues = useMemo(
    () => ({
      categories: filters.categoryIds?.map((id) => id.toString()) || [],
      country: filters.country ? parseLocationsString(filters.country) : [],
      status:
        filters.isActive === true ? 'active' : filters.isActive === false ? 'inactive' : 'all',
    }),
    [filters.categoryIds, filters.country, filters.isActive]
  );

  const handleApplyFilters = (values: VendorFilterPopoverValues) => {
    // Always set categoryIds (empty array if no categories selected)
    const categoryIds = (values.categories || []).map((c) => parseInt(c, 10));
    setCategoryIds(categoryIds);

    // Convert cascader paths back to the API location string (most specific segment)
    const countryValue = (values.country || [])
      .map((path) => String(path[path.length - 1]))
      .join(',');
    setCountry(countryValue);

    // Set isActive based on status (null when 'all')
    if (values.status === 'active') {
      setIsActive(true);
    } else if (values.status === 'inactive') {
      setIsActive(false);
    } else {
      setIsActive(null);
    }
  };

  // Vendor mutations
  const { toggleStatus } = useVendorMutations();

  // Handle activate/deactivate with confirmation
  const handleToggleStatus = (vendor: VendorListItem) => {
    toggleStatusDialog.open(vendor);
  };

  const baseColumns: TableProps<VendorListItem>['columns'] = [
    {
      title: t('vendor.fields.vendorId'),
      dataIndex: 'vendorId',
      key: 'vendorId',
      width: 120,
      sorter: true,
      showSorterTooltip: false,
      render: (vendorId: string) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>
          <HighlightText text={vendorId} searchTerm={filters.search} fallback="-" />
        </span>
      ),
    },
    {
      title: t('vendor.fields.vendorName'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 250,
      sorter: true,
      showSorterTooltip: false,
      render: (_: string, record: VendorListItem) => {
        const hasSecondaryInfo = record.nickname || record.alternativeName;

        return (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span style={{ fontWeight: token.fontWeightStrong }}>
              <HighlightText text={record.vendorName} searchTerm={filters.search} fallback="-" />
            </span>
            {hasSecondaryInfo && (
              <span
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorTextSecondary,
                  lineHeight: 1.3,
                }}
              >
                {record.nickname && record.alternativeName ? (
                  <>
                    <HighlightText text={record.nickname} searchTerm={filters.search} fallback="" />
                    {' · '}
                    <HighlightText
                      text={record.alternativeName}
                      searchTerm={filters.search}
                      fallback=""
                    />
                  </>
                ) : (
                  <HighlightText
                    text={record.nickname || record.alternativeName}
                    searchTerm={filters.search}
                    fallback=""
                  />
                )}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: t('vendor.fields.category'),
      dataIndex: 'categories',
      key: 'categories',
      width: 180,
      sorter: false,
      render: (categories: VendorListItem['categories']) => {
        if (!categories || categories.length === 0) return '-';

        // Normalize categories and map colors from category list
        const normalizedCategories = normalizeVendorCategories(categories, categoryColorMap);

        // Show first 2 categories, with +N indicator if more
        const visibleCategories = normalizedCategories.slice(0, 2);
        const remainingCategories = normalizedCategories.slice(2);
        const remainingCount = remainingCategories.length;

        return (
          <div className="tw-flex tw-gap-1 tw-flex-wrap tw-items-center">
            {visibleCategories.map((cat) => (
              <CategoryTag
                key={cat.categoryId || cat.category}
                name={cat.category}
                color={cat.color || token.colorPrimary}
              />
            ))}
            {remainingCount > 0 && (
              <Tooltip
                title={
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    {remainingCategories.map((cat) => (
                      <CategoryTag
                        key={cat.categoryId || cat.category}
                        name={cat.category}
                        color={cat.color || token.colorPrimary}
                      />
                    ))}
                  </div>
                }
                color={token.colorBgElevated}
                styles={{
                  body: {
                    color: token.colorText,
                  },
                }}
              >
                <Tag
                  bordered={false}
                  style={{
                    backgroundColor: token.colorFillQuaternary,
                    color: token.colorTextSecondary,
                    borderRadius: radiusTokens.control,
                    fontSize: token.fontSizeSM,
                    cursor: 'pointer',
                    border: 'none',
                    margin: 0,
                  }}
                >
                  +{remainingCount}
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: t('vendor.fields.abn'),
      dataIndex: 'abn',
      key: 'abn',
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      render: (abn: string) => (
        <HighlightText text={abn} searchTerm={filters.search} fallback="-" />
      ),
    },
    {
      title: t('vendor.fields.address'),
      dataIndex: 'address',
      key: 'address',
      width: 250,
      sorter: true,
      showSorterTooltip: false,
      render: (address: string) => renderDash(address),
    },
    {
      title: t('vendor.fields.phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      render: (phoneNumber: string) => renderDash(phoneNumber),
    },
    {
      title: t('common:createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (createdAt: string) =>
        createdAt ? <TimestampDisplay date={createdAt} showLabel={false} /> : '-',
    },
    {
      title: t('common:updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 130,
      render: (updatedAt: string) =>
        updatedAt ? <TimestampDisplay date={updatedAt} showLabel={false} /> : '-',
    },
    {
      title: t('vendor.fields.action'),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record: VendorListItem) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: t('vendor.actions.edit'),
                icon: <EditOutlined />,
                disabled: !record.isActive,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate(`/auth/vendors/${record.vendorId}/edit`);
                },
              },
              {
                type: 'divider',
              },
              {
                key: 'toggle-status',
                label: record.isActive
                  ? t('vendor.actions.deactivate')
                  : t('vendor.actions.activate'),
                icon: record.isActive ? <StopOutlined /> : <CheckCircleOutlined />,
                danger: record.isActive === true,
                style: !record.isActive ? { color: token.colorSuccess } : undefined,
                disabled: record.isActive === undefined || record.isActive === null,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleToggleStatus(record);
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  // Filter columns based on props
  const allColumns = showCategoryColumn
    ? baseColumns
    : baseColumns.filter((col) => col.key !== 'categories');

  return (
    <div className={compact ? '' : 'tw-px-6 tw-pb-8'}>
      {/* Inactive row styling */}
      <style>
        {`
          /* Inactive row styling - uniform dimmed appearance */
          /* Must target both tr AND td because Ant Design sets background on cells */
          /* Use .production-enhanced prefix for specificity (from TableContainer) */
          .production-enhanced .ant-table-tbody > tr.inactive-row,
          .production-enhanced .ant-table-tbody > tr.inactive-row > td {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row:hover > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:active > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:focus > td {
            background-color: ${token.colorFillQuaternary} !important;
          }

          /* Individual cell hover - match row hover */
          .production-enhanced .ant-table-tbody > tr.inactive-row > td:hover {
            background-color: ${token.colorFillQuaternary} !important;
          }

          /* Keep background when dropdown is open */
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td:hover {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row td {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row a {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:hover,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:focus,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:active {
            color: ${token.colorTextTertiary} !important;
            background: transparent !important;
            border-color: transparent !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .anticon {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn .anticon {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-tag {
            opacity: 0.5;
            filter: grayscale(100%);
          }

          /* Gray out category color dots in inactive rows */
          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="border-radius: 50%"],
          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="borderRadius"] {
            background-color: ${token.colorTextTertiary} !important;
            opacity: 0.5;
          }
        `}
      </style>

      {/* Include Inactive checkbox for compact mode */}
      {compact && (
        <div className="tw-mb-4 tw-flex tw-justify-end">
          <Checkbox
            checked={filters.isActive === null}
            onChange={(e) => setIsActive(e.target.checked ? null : true)}
          >
            {t('vendor.labels.includeInactiveVendors')}
          </Checkbox>
        </div>
      )}

      {/* Header Section */}
      {!compact && (
        <div className="tw-mb-6">
          <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
            {t('vendor.list.title')}
          </h1>
        </div>
      )}

      {/* Search and Filter Section */}
      {!compact && (
        <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
          <Input
            placeholder={t('vendor.list.search')}
            value={filters.search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="large"
            suffix={
              <SearchOutlined
                style={{
                  fontSize: token.sizeMD,
                  color: token.colorPrimary,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            }
            style={{
              width: '450px',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              transition: 'all 0.3s ease',
            }}
            styles={{
              input: {
                borderRadius: radiusTokens.card,
              },
            }}
          />
          <GenericFilterPopover<VendorFilterPopoverValues>
            open={filterPopoverOpen}
            onOpenChange={setFilterPopoverOpen}
            onApply={handleApplyFilters}
            initialFilters={initialPopoverFilters}
            fields={filterFields}
            applyText={t('vendor.filter.apply')}
            cancelText={t('vendor.filter.cancel')}
          >
            <Badge count={activeFilterCount}>
              <Button
                icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
                size="large"
                style={{
                  borderRadius: radiusTokens.card,
                  height: `${controlHeightTokens.controlHeightXL}px`,
                  width: `${controlHeightTokens.controlHeightXL}px`,
                  border: `1px solid ${token.colorBorder}`,
                  transition: 'all 0.3s ease',
                }}
                styles={{
                  icon: { fontSize: token.sizeMD },
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = token.colorPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = token.colorBorder;
                }}
              />
            </Badge>
          </GenericFilterPopover>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => navigate('/auth/vendors/add')}
            style={{
              marginLeft: 'auto',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              paddingLeft: token.paddingLG,
              paddingRight: token.paddingLG,
            }}
          >
            {t('vendor.list.create')}
          </Button>
        </div>
      )}

      {/* Table — S0.3 state triad baked in:
          error+retry via TableContainer.errorState,
          first-use empty (+ create CTA) and filter-zero-results (+ clear filters)
          via ListEmptyState in the Table empty slot */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('vendor.messages.fetchFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<VendorListItem>
          {...tableProps}
          columns={allColumns}
          rowKey="vendorId"
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => {
              navigate(`/auth/vendors/${record.vendorId}`);
            },
            style: {
              cursor: 'pointer',
            },
          })}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('vendor.list.emptyWithFiltersTitle')}
                actionLabel={compact ? undefined : t('common:actions.clearFilters')}
                onAction={compact ? undefined : resetFilters}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={t('vendor.list.emptyNoDataTitle')}
                actionLabel={compact ? undefined : t('vendor.list.createFirst')}
                onAction={compact ? undefined : () => navigate('/auth/vendors/add')}
                actionIcon={<PlusOutlined />}
              />
            ),
          }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: false,
          }}
          scroll={{ x: 1200 }}
        />
      </TableContainer>

      {/* Confirmation Dialog for Activate/Deactivate */}
      <ConfirmationDialog
        open={toggleStatusDialog.isOpen}
        onClose={toggleStatusDialog.close}
        onConfirm={async () => {
          const vendor = toggleStatusDialog.data;
          if (vendor && vendor.isActive !== undefined && vendor.isActive !== null) {
            try {
              const newStatus = !vendor.isActive;

              // Call API to toggle vendor status
              await toggleStatus(vendor.vendorId, newStatus);

              const action = vendor.isActive
                ? t('vendor.status.inactive').toLowerCase()
                : t('vendor.status.active').toLowerCase();
              message.success(t('vendor.messages.vendorActionSuccess', { action }));

              // Refresh vendor list to show updated status
              refresh();
            } catch (error) {
              message.error(t('vendor.messages.statusUpdateFailed'));
              console.error('Error toggling vendor status:', error);
            }
          }
          toggleStatusDialog.close();
        }}
        title={
          toggleStatusDialog.data?.isActive
            ? t('vendor.modals.deactivateVendor')
            : t('vendor.notifications.activateVendor')
        }
        message={t('vendor.messages.confirmStatusChange', {
          action: toggleStatusDialog.data?.isActive
            ? t('vendor.actions.deactivate').toLowerCase()
            : t('vendor.actions.activate').toLowerCase(),
          name: toggleStatusDialog.data?.vendorName || '',
        })}
        type={toggleStatusDialog.data?.isActive ? 'danger' : 'info'}
        confirmText={
          toggleStatusDialog.data?.isActive
            ? t('vendor.actions.deactivate')
            : t('vendor.actions.activate')
        }
      />
    </div>
  );
};

export default VendorListPage;
